import React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";

const Hero2Section = styled.section`
  width: 100%;
  min-height: 60vh;
  margin-top: -50px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  background: #04091d url("/hero2.JPG") center center / cover no-repeat;
  color: #fff;
  overflow: hidden;
  aspect-ratio: 16/7;

  @media (max-width: 768px) {
    aspect-ratio: unset;
    min-height: 50vh;
    align-items: flex-start;
    background-size: cover;
    background-position: center;
  }
`;

const Hero2Overlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1;
`;

const Hero2Content = styled.div`
  position: relative;
  z-index: 2;
  max-width: 720px;
  padding: 0 2rem 0 3vw;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  min-height: 60vh;

  @media (max-width: 1024px) {
    max-width: 560px;
  }

  @media (max-width: 768px) {
    padding: 6rem 3rem 2rem 0.5rem;
    max-width: 80%;
    min-height: unset;
    justify-content: flex-start;
    align-items: flex-start;
  }
`;

const Headline = styled.h1`
  font-family: sans-serif;
  font-size: 2.3rem;
  color: #fff;
  margin-bottom: 1rem;
  text-shadow: 0 2px 16px rgba(0, 0, 0, 0.25);

  @media (max-width: 768px) {
    font-size: 1.7rem;
    line-height: 1.2;
  }

  @media (max-width: 480px) {
    font-size: 1.5rem;
  }
`;

const SubHeadline = styled.p`
  font-size: 1.25rem;
  color: #fff;
  margin-bottom: 2rem;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);

  @media (max-width: 768px) {
    font-size: 1.1rem;
    line-height: 1.5;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const CTAButton = styled(Link)`
  display: inline-block;
  background: #e97d7dff;
  color: #fff;
  font-size: 1.1rem;
  font-weight: 600;
  padding: 0.9rem 2.2rem;
  border-radius: 2rem;
  text-decoration: none;
  box-shadow: 0 2px 8px rgba(233, 125, 125, 0.08);
  transition: background 0.2s, color 0.2s;
  margin-right: 1rem;
  margin-bottom: 1rem;

  &:hover {
    background: #fff;
    color: #e97d7dff;
    border: 2px solid #e97d7dff;
    cursor: pointer;
  }

  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 0.8rem 2rem;
    margin-right: 0.8rem;
  }

  @media (max-width: 480px) {
    font-size: 0.95rem;
    padding: 0.7rem 1.8rem;
    margin-right: 0.5rem;
    margin-bottom: 0.8rem;
  }
`;

const SecondaryCTAButton = styled.a`
  display: inline-block;
  background: transparent;
  color: #fff;
  font-size: 1.1rem;
  font-weight: 600;
  padding: 0.9rem 2.2rem;
  border-radius: 2rem;
  text-decoration: none;
  border: 2px solid #fff;
  transition: all 0.2s;
  margin-bottom: 1rem;

  &:hover {
    background: #fff;
    color: #e97d7dff;
  }

  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 0.8rem 2rem;
  }

  @media (max-width: 480px) {
    font-size: 0.95rem;
    padding: 0.7rem 1.8rem;
    margin-bottom: 0.8rem;
  }
`;

const SocialProof = styled.div`
  display: flex;
  align-items: center;
  margin-top: 1.5rem;
  font-size: 0.95rem;
  color: #fff;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);

  @media (max-width: 768px) {
    font-size: 0.9rem;
    margin-top: 1.2rem;
  }

  @media (max-width: 480px) {
    display: none;
  }
`;

const StarIcon = styled.span`
  color: #ffd700;
  margin-right: 0.3rem;
  font-size: 1.1em;
`;

const HandcraftedNote = styled.p`
  font-size: 0.9rem;
  color: #e8e8e8;
  margin-top: 0.8rem;
  font-style: italic;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);

  @media (max-width: 768px) {
    font-size: 0.85rem;
    margin-top: 0.6rem;
  }

  @media (max-width: 480px) {
    font-size: 0.8rem;
    margin-top: 0.5rem;
  }
`;

const CTAButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.8rem;
  }
`;

const Hero2: React.FC = () => {
  return (
    <Hero2Section>
      <Hero2Overlay />
      <Hero2Content>
        <Headline>Discover Your Unique Shine</Headline>
        <SubHeadline>
          Elegant, handcrafted jewelry for every story. Celebrate your
          individuality with our exclusive collection.
        </SubHeadline>
        <CTAButtons>
          <CTAButton to="/shop">View Collection</CTAButton>
        </CTAButtons>
        <SocialProof>
          <StarIcon>⭐</StarIcon>
          <span>Loved by 1,200+ happy customers | 4.9/5 average rating</span>
        </SocialProof>
      </Hero2Content>
    </Hero2Section>
  );
};

export default Hero2;
